import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import { useLanguage } from '../../context/LanguageContext';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    @media (max-width: 360px) {
      grid-template-columns: repeat(2, minmax(120px, 1fr));
      grid-gap: 0 5px;
    }

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: brightness(100%);
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: normal;
      filter: brightness(80%);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: transparent; // 改为透明
      mix-blend-mode: normal; // 改为normal
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { language } = useLanguage();
  const isZh = language === 'zh';

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = isZh
    ? [
      'Python',
      'AI 工具',
      'Excel 自动化',
      'Bloomberg Terminal',
      'Wind Terminal',
      '财务建模',
      '数据分析',
      'Git',
      '网页开发',
    ]
    : [
      'Python',
      'AI Tools',
      'Excel Automation',
      'Bloomberg Terminal',
      'Wind Terminal',
      'Financial Modeling',
      'Data Analysis',
      'Git',
      'Web Development',
    ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">{isZh ? '关于我' : 'About Me'}</h2>

      <div className="inner">
        <StyledText>
          <div>
            {isZh ? (
              <>
                <p>
                  你好！我叫君宜，热衷于为复杂的问题找到优雅的解决方案。我喜欢在金融与技术的交叉地带工作，在那里我可以把分析性思维与务实的创新结合起来。
                </p>

                <p>
                  在<a href="#jobs">证券公司</a>
                  做股票研究实习期间，我参与行业分析和投资报告，同时发现了自己在流程优化方面的天赋。我开发的自动化工具显著提升了团队的数据处理效率，并获得了主管的认可。这些经历让我看到技术如何能够增强传统的金融分析。
                </p>

                <p>
                  最近，我一直在探索自己的<a href="#explorations">兴趣并拓展能力</a>
                  。我学习了新的语言，尝试了各种 AI 工具来提升编程技能，还做了一些项目，比如{' '}
                  <a
                    href="https://github.com/LeaiFish/PersonalWebsite"
                    target="_blank"
                    rel="noopener noreferrer">
                    这个个人网站
                  </a>
                  。我追随自己的好奇心去发展技术能力，同时与新兴技术和趋势保持同步。
                </p>

                <p>
                  我在能够观察、理解全局、进而做出有意义改进的环境中如鱼得水。无论是数据分析、流程优化，还是学习新技术，让工作变得更高效、更有影响力的机会都让我充满动力。
                </p>

                <p>以下是我最近在使用的一些技术：</p>
              </>
            ) : (
              <>
                <p>
                  Hello! My name is Junyi and I'm passionate about finding elegant solutions to
                  complex problems. I enjoy working at the intersection of finance and technology,
                  where I can combine analytical thinking with practical innovation.
                </p>

                <p>
                  During my equity research internships at <a href="#jobs">securities firms</a>, I
                  worked on industry analysis and investment reports while discovering my talent for
                  process optimization. I developed automation tools that significantly improved our
                  team's data processing efficiency and earned recognition from supervisors. These
                  experiences showed me how technology can enhance traditional financial analysis.
                </p>

                <p>
                  Recently, I've been exploring my{' '}
                  <a href="#explorations">interests and expanding my capabilities</a>. I've learned
                  new languages, experimented with various AI tools to enhance my programming
                  skills, and built projects like{' '}
                  <a
                    href="https://github.com/LeaiFish/PersonalWebsite"
                    target="_blank"
                    rel="noopener noreferrer">
                    this personal website
                  </a>
                  . I'm following my curiosity to develop technical abilities while staying
                  connected to emerging technologies and trends.
                </p>

                <p>
                  I thrive in environments where I can observe, understand the full picture, and
                  then contribute meaningful improvements. Whether it's data analysis, process
                  optimization, or learning new technologies, I'm motivated by the opportunity to
                  make work more efficient and impactful.
                </p>

                <p>Here are a few technologies I've been working with recently:</p>
              </>
            )}
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
